import { safeMartinaSourceHref } from '../../../../shared/martinaSourceLinks';
export { safeMartinaSourceHref } from '../../../../shared/martinaSourceLinks';

export type MartinaSourceLink = { key: string; label: string; href: string | null };
export type MartinaWebSource = { url: string; title: string; citedText?: string };

const SOURCE_LABELS: Record<string, string> = {
  task: 'Attività', tasks: 'Attività', meeting: 'Appuntamento', meetings: 'Appuntamento',
  email_message: 'Email', email_messages: 'Email', email_thread: 'Conversazione email',
  company: 'Azienda', contact: 'Contatto', deal: 'Opportunità', lead: 'Lead',
  file: 'File', branch: 'Filiale', brand: 'Brand', synergy: 'Sinergia', note: 'Nota',
};

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

function legacySourceHref(source: Record<string, unknown>): string | null {
  const id = String(source.sourceId ?? '');
  if (!/^[1-9]\d*$/.test(id)) return null;
  const kind = source.sourceKind;
  if (kind === 'task' || kind === 'tasks') return `/tasks?taskId=${id}`;
  if (kind === 'email_message' || kind === 'email_messages') return `/emails?emailId=${id}`;
  if (kind === 'email_thread') return `/emails?threadId=${id}`;
  if (kind === 'file') return `/files?fileId=${id}`;
  if (kind === 'brand') return `/brands?brandId=${id}`;
  const routes: Record<string, string> = { meeting: 'meetings', meetings: 'meetings', company: 'companies', contact: 'contacts', deal: 'deals', lead: 'leads', branch: 'branches' };
  const route = typeof kind === 'string' ? routes[kind] : null;
  return route ? `/${route}/${id}` : null;
}

function readableLabel(source: Record<string, unknown>): string {
  const label = typeof source.label === 'string' ? source.label.trim() : '';
  // A technical event identifier is not a useful citation title.
  if (label && label !== source.eventType && label !== source.citationId && label !== source.eventId && !/^[\w]+(?:[_.:][\w]+)+$/.test(label)) return label;
  return typeof source.sourceKind === 'string' ? SOURCE_LABELS[source.sourceKind] || 'Fonte CRM' : 'Fonte CRM';
}

/** No last-turn or conversation-wide sources: each answer owns its citations. */
export function memorySourcesForMessage(message: { contentBlocks?: unknown }): MartinaSourceLink[] {
  if (!Array.isArray(message.contentBlocks)) return [];
  const result: MartinaSourceLink[] = [];
  const seen = new Set<string>();
  for (const value of message.contentBlocks) {
    const block = record(value);
    if (block?.type !== 'memory_sources' || !Array.isArray(block.sources)) continue;
    for (const item of block.sources) {
      const source = record(item);
      if (!source || typeof source.sourceKind !== 'string' || typeof source.sourceId !== 'string') continue;
      const href = source.href == null || source.href === ''
        ? legacySourceHref(source) : safeMartinaSourceHref(source.href);
      const label = readableLabel(source);
      const key = typeof source.eventId === 'string' && source.eventId
        ? source.eventId : `${source.sourceKind}:${source.sourceId}:${href ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ key, label, href });
    }
  }
  return result;
}

export function webSourcesForDisplay(sources: unknown): MartinaWebSource[] {
  if (!Array.isArray(sources)) return [];
  return sources.flatMap((value) => {
    const source = record(value);
    const href = safeMartinaSourceHref(source?.url);
    if (!source || !href?.startsWith('https://')) return [];
    return [{ url: href, title: typeof source.title === 'string' && source.title.trim() ? source.title.trim() : new URL(href).hostname,
      ...(typeof source.citedText === 'string' ? { citedText: source.citedText } : {}) }];
  });
}
